import { z } from 'zod';

/**
 * PRE-107: admin transactional email contracts (Resend templates + send +
 * delivery history). Admin-only send/list; webhook status updates are public
 * but signature-gated on the API side.
 */

/** Delivery ledger statuses we persist (open/click intentionally omitted). */
export const EMAIL_SEND_STATUSES = [
  'queued',
  'sent',
  'delivered',
  'delivery_delayed',
  'bounced',
  'complained',
  'failed',
  'suppressed',
] as const;
export const emailSendStatusSchema = z.enum(EMAIL_SEND_STATUSES);
export type EmailSendStatus = z.infer<typeof emailSendStatusSchema>;

/** BR-20 style normalize: trim + lowercase, max 254. */
export const emailAddressSchema = z.string().trim().toLowerCase().email().max(254);

export const emailTemplateVariableSchema = z.object({
  key: z.string().min(1).max(50),
  type: z.enum(['string', 'number']),
  fallbackValue: z.union([z.string(), z.number(), z.null()]).optional(),
});
export type EmailTemplateVariable = z.infer<typeof emailTemplateVariableSchema>;

/** Published Resend template summary (GET /v1/admin/email-templates). */
export const emailTemplateSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  alias: z.string().nullable(),
  status: z.literal('published'),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EmailTemplateSummary = z.infer<typeof emailTemplateSummarySchema>;

/** Full template detail including variable keys for the compose form. */
export const emailTemplateDetailSchema = emailTemplateSummarySchema.extend({
  subject: z.string().nullable(),
  from: z.string().nullable(),
  replyTo: z.array(z.string()).nullable().optional(),
  variables: z.array(emailTemplateVariableSchema),
});
export type EmailTemplateDetail = z.infer<typeof emailTemplateDetailSchema>;

/**
 * POST /v1/admin/emails body. Two mutually-exclusive modes:
 *
 *  - Template mode: `templateId` (Resend published template) + `variables`.
 *  - Plain mode: `subject` + `text` (+ optional `html`), no template.
 *
 * Exactly one mode must be used. Template variables are string|number only
 * (Resend template variable types).
 */
export const sendEmailInputSchema = z
  .object({
    to: emailAddressSchema,
    /** Resend template id or alias (template mode). */
    templateId: z.string().trim().min(1).max(256).optional(),
    variables: z
      .record(z.string().min(1).max(50), z.union([z.string().max(2000), z.number()]))
      .default({}),
    /** Plain-mode subject line. */
    subject: z.string().trim().min(1).max(200).optional(),
    /** Plain-mode plain-text body. */
    text: z.string().min(1).max(50000).optional(),
    /** Plain-mode optional HTML body. */
    html: z.string().min(1).max(100000).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.templateId) {
      if (data.subject || data.text || data.html) {
        ctx.addIssue({
          code: 'custom',
          message: 'Provide either templateId or subject/text — not both',
          path: ['templateId'],
        });
      }
      return;
    }
    // Plain mode
    if (!data.subject) {
      ctx.addIssue({ code: 'custom', message: 'subject is required', path: ['subject'] });
    }
    if (!data.text) {
      ctx.addIssue({ code: 'custom', message: 'text is required', path: ['text'] });
    }
  });
export type SendEmailInput = z.infer<typeof sendEmailInputSchema>;

/** One row from the delivery ledger (admin history). */
export const emailSendSchema = z.object({
  id: z.string(),
  status: emailSendStatusSchema,
  toEmail: z.string(),
  fromEmail: z.string(),
  subject: z.string().nullable(),
  /** Null for plain-mode sends (no Resend template). */
  templateId: z.string().nullable(),
  templateAlias: z.string().nullable(),
  templateName: z.string().nullable(),
  provider: z.literal('resend'),
  providerMessageId: z.string().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  userId: z.string().nullable(),
  sentAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EmailSend = z.infer<typeof emailSendSchema>;

/** GET /v1/admin/emails query. Cursor is opaque (createdAt|id). */
export const listEmailSendsQuerySchema = z.object({
  status: emailSendStatusSchema.optional(),
  to: emailAddressSchema.optional(),
  templateId: z.string().trim().min(1).max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(512).optional(),
});
export type ListEmailSendsQuery = z.infer<typeof listEmailSendsQuerySchema>;

export const listEmailSendsResponseSchema = z.object({
  items: z.array(emailSendSchema),
  nextCursor: z.string().nullable(),
});
export type ListEmailSendsResponse = z.infer<typeof listEmailSendsResponseSchema>;

export const sendEmailResponseSchema = z.object({
  send: emailSendSchema,
  /** True when an existing row was returned for the same Idempotency-Key. */
  replayed: z.boolean(),
});
export type SendEmailResponse = z.infer<typeof sendEmailResponseSchema>;
